import { intersection } from "lodash-es";
import { firstBy } from "thenby";
import type { Simplify } from "type-fest";

import { dayjs } from "@thalamos/common";
import type {
  Accreditation,
  BaseFormTemplate,
  ExtendedThalamosUser,
  ExtendedWorkItem,
  FormContextData,
  FormContextMiniForm,
  FormPartTemplate,
  GenericFormData,
  H3Part1DataSchema,
  Team,
  UserOrTeamRequirement,
  UUID,
  WorkItemMiniForm,
} from "@thalamos/aspire-common";
import {
  applicationTemplateIds,
  AUTO_CLOSE_NO_MED_REC_REASON,
  COMPLETED_FORM_STATUSES,
  formHasSignatures,
  formIsApplication,
  formIsCollapsedRedoReplacement,
  formIsCompleted,
  formIsDeMinimisEligible,
  formIsInProgress,
  formIsMedRec,
  formIsRecordOfDetention,
  formIsUnderRedo,
  formIsValidForProgression,
  formIsVoid,
  getBaseFormTemplate,
  getFullFormName,
  getLatestVersion,
  isFirstSignatory,
  isGuestUserSession,
  liveMcaForms,
  mcaContextPrompt,
  medicalRecommendationTemplateIds,
  meetsRegistrationNumberRequirement,
  recordOfDetentionTemplateIds,
  redoBasisIsValid,
  redoBasisUpstreamIds,
} from "@thalamos/aspire-common";

import { config } from "../../../config.js";
import {
  hasNoActiveWorkItemOrRejectedCancelledOrCompleted,
  workItemIsAnotherWorkflow,
} from "./workItemHelpers.js";

interface AdmissionsFormData {
  template: BaseFormTemplate;
  genericFormData: GenericFormData;
  isMedicalRecommendation: boolean;
  latestVersionHasSignatures: boolean;
  formNeedsMoreSignatures: boolean;
  myFormWorkItem: WorkItemMiniForm | undefined;
  canLaunchDraft: boolean;
  canRequestAmend: boolean;
  canRequestDeMinimis: boolean;
  canRequestRedo: boolean;
  canRedoApplicationUnderRedo: boolean;
  isFormFinalised: boolean;
  isAmend: boolean;
  draftIdOnNextPart: UUID | null;
}

interface UserAndWorkItemState {
  amInTeamContext: boolean;
  myTeamId?: UUID;
  userId: UUID;
  teamActiveWorkItem?: ExtendedWorkItem;
  teamActiveOrArchivedWorkItem?: ExtendedWorkItem;
  myActiveWorkItem?: ExtendedWorkItem;
  inferredTeamType?: Team["type"];
  inferredAccreditationTypes: Accreditation["type"][];
  matchesSigningReqs: (reqs: UserOrTeamRequirement) => boolean;
  amGuest: boolean;
  amAmhpUser: boolean;
  amDoctor: boolean;
  amMha: boolean;
  amInWardTeam: boolean;
  isAcdContext: boolean;
}

export function computeUserAndWorkItemState(
  user: ExtendedThalamosUser,
  workItem: ExtendedWorkItem | undefined,
): UserAndWorkItemState {
  const isAcdContext = !!user.sessionOrganisationConfiguration?.acdEnabled;

  // Work item state
  const workItemIsActive = workItem?.status === "accepted";
  const workItemIsActiveOrArchived = ["accepted", "completed"].includes(
    workItem?.status!,
  );
  const amAssignedToWorkItem = workItem?.assignedUserId === user.id;

  const teamActiveWorkItem = workItemIsActive ? workItem : undefined;
  const teamActiveOrArchivedWorkItem = workItemIsActiveOrArchived
    ? workItem
    : undefined;
  const myActiveWorkItem = amAssignedToWorkItem
    ? teamActiveWorkItem
    : undefined;

  const workItemForm = workItem?.forms.find((f) => f.id === workItem?.formId);

  // Request state
  const requestType = workItem?.requestType;
  const isMedRecRequest = requestType === "medical-recommendation";
  const isApplicationRequest = requestType === "application";
  const isRecordOfDetentionRequest = requestType === "record-of-detention";
  const isFinaliseRequest = requestType === "finalise";
  const isPart2MedRecRequest = !!workItemForm && formIsMedRec(workItemForm);

  const myTeamType = user.sessionContext?.teamType;
  const amGuest = isGuestUserSession(user);
  const amGuestAmph = amGuest && isApplicationRequest;
  const amGuestDoctor = amGuest && (isMedRecRequest || isPart2MedRecRequest);
  const amGuestMha = amGuest && isFinaliseRequest;
  const amGuestWardTeamMember = amGuest && isRecordOfDetentionRequest;

  // User accreditations taking into account those that have been inferred from their guest request type
  const inferredAccreditationTypes = [
    ...(user.accreditations?.map((acc) => acc.type) ?? []),
    amGuestAmph && "amhp",
    amGuestDoctor && "doctor",
    // Everyone in an org with ACD enabled can do an ACD for now, as this feels like the least disgusting hack
    isAcdContext && "acd-facilitator",
  ].filter(Boolean) as Accreditation["type"][];

  // User team memberships taking into account those that have been inferred from their guest request type
  const inferredTeamType = (
    amGuest
      ? (amGuestMha && "mha") || (amGuestWardTeamMember && "ward") || undefined
      : myTeamType
  ) as Team["type"] | undefined;

  const matchesSigningReqs = (reqs: UserOrTeamRequirement): boolean => {
    const hasValidAccreditation = !!intersection(
      reqs.accreditation ?? [],
      inferredAccreditationTypes,
    ).length;
    const hasProhibitedAccreditation = !!intersection(
      reqs.prohibitedAccreditations ?? [],
      inferredAccreditationTypes,
    ).length;
    const hasValidRegistrationNumber = meetsRegistrationNumberRequirement(
      user.accreditations,
      reqs,
    );
    const hasValidTeam = !!reqs.teamType?.some((t) => t === inferredTeamType);
    const hasProhibitedTeam = !!reqs.prohibitedTeamTypes?.some(
      (t) => t === inferredTeamType,
    );
    // When a non-empty allOf is present the positive gate is the conjunction of
    // every sub-requirement; top-level prohibited* checks still apply alongside
    // it. An empty/absent allOf falls back to the normal OR gate (so allOf: []
    // never vacuously grants access via [].every()).
    const positiveMet =
      reqs.allOf && reqs.allOf.length > 0
        ? reqs.allOf.every((r) => matchesSigningReqs(r))
        : hasValidAccreditation || hasValidTeam;
    return (
      positiveMet &&
      hasValidRegistrationNumber &&
      !hasProhibitedAccreditation &&
      !hasProhibitedTeam
    );
  };

  return {
    teamActiveWorkItem,
    myActiveWorkItem,
    teamActiveOrArchivedWorkItem,
    amInTeamContext: user.sessionContext?.type === "team",
    myTeamId: user.sessionContext?.teamId,
    userId: user.id,
    inferredTeamType,
    inferredAccreditationTypes,
    matchesSigningReqs,
    amGuest,
    amAmhpUser: inferredAccreditationTypes.includes("amhp"),
    amDoctor: inferredAccreditationTypes.includes("doctor"),
    amMha: inferredTeamType === "mha",
    amInWardTeam: inferredTeamType === "ward",
    isAcdContext,
  };
}

/**
 * Who may sign part 1 of each of the given templates. A template with no known
 * base or no parts contributes nothing rather than blocking the whole check.
 */
function firstPartSigningReqs(
  templateIds: readonly string[],
): UserOrTeamRequirement[] {
  return templateIds.flatMap((id) => {
    const signing = getBaseFormTemplate(id)?.parts[0]?.signing;
    return signing === undefined ? [] : [signing.userOrTeamRequirement];
  });
}

export function getCompletedApplication(
  forms: FormContextMiniForm[] | WorkItemMiniForm[],
) {
  const applications = forms.filter(formIsApplication);
  return applications.find(formIsCompleted);
}

/**
 * Whether the Record of Detention (H3) launch button should be offered for an
 * admission. It is offered whenever a completed application exists to base an H3
 * on, and no H3 is already in progress or part-one-signed.
 *
 * It deliberately ignores the redo state of the application: a completed
 * application that is under redo (or whose med rec basis is under redo) is still
 * a valid launch basis. The linked-forms modal — not this gate — decides which
 * application is selectable (only completed, non-redo ones), and signing on an
 * invalid basis is blocked at sign time. Gating the button on redo state would
 * strand the user even when a valid application exists. Voided applications are
 * superseded and never count.
 */
export function canCreateRecordOfDetention(
  forms: FormContextMiniForm[],
): boolean {
  const live = forms.filter((f) => !formIsVoid(f));
  const recordOfDetentions = live.filter(formIsRecordOfDetention);
  const hasInProgressRecordOfDetention = recordOfDetentions.some(
    (d) => d.status === "in-progress",
  );
  const recordOfDetentionPartOneSigned = recordOfDetentions.some(
    (d) => !!formHasSignatures(d),
  );
  return (
    !hasInProgressRecordOfDetention &&
    !recordOfDetentionPartOneSigned &&
    !!getCompletedApplication(live)
  );
}

// True when the AMHP has recorded "I will not make an application" on this
// admission. status === "closed" is the lifecycle gate (handles reopen, which
// flips it back to "active" while leaving the closedBy fields sticky);
// closedByUserId/closedByTeamId are the discriminator — written only by
// AdmissionController.recordReasonForNotProceedingAppForm, never by the other
// close paths (H3 finalisation, H3 expiry, SOAD/police finalise, standalone
// complete). See ~/.claude/plans/can-you-explain-to-reactive-hoare.md.
export function hasNoApplicationDecided(formContext: {
  status?: FormContextData["status"];
  closedByUserId?: string;
  closedByTeamId?: string;
}): boolean {
  return (
    formContext.status === "closed" &&
    !!(formContext.closedByUserId || formContext.closedByTeamId)
  );
}

// True when the admission was auto-closed 28 days after a No Med Rec was
// recorded. status === "closed" is the lifecycle gate (handles reopen, which
// flips it back to "active" while leaving closedReason sticky); closedReason
// is the discriminator — only this job writes the AUTO_CLOSE_NO_MED_REC_REASON
// value. Note this never overlaps with hasNoApplicationDecided because the
// finder query skips formContexts that are already closed.
export function hasNoMedRecAutoClosed(formContext: {
  status?: FormContextData["status"];
  closedReason?: string;
}): boolean {
  return (
    formContext.status === "closed" &&
    formContext.closedReason === AUTO_CLOSE_NO_MED_REC_REASON
  );
}

// Unarchiving returns an item to "accepted" but leaves a recorded reason in
// place, so the reason alone would keep reporting a decision that has since
// been undone; the status gate is what makes this answer the present state.
export function workItemRecordsNoMedRec(
  workItem: { status: string; reason?: string } | undefined,
): boolean {
  return workItem?.status === "completed" && !!workItem.reason;
}

// True when at least one doctor on this admission has recorded "I will not
// make a medical recommendation".
export function hasAnyDoctorDecidedNoMedRec(
  workItems: Array<{
    status: string;
    reason?: string;
  }>,
): boolean {
  return workItems.some(workItemRecordsNoMedRec);
}

/**
 * The work item on this form context that the caller's active team context may
 * act on, or undefined when that team holds none.
 *
 * A work item is only ever actionable by the team holding it, and a request
 * from any other team is refused as "not found" so callers cannot probe for
 * records beyond their reach. One chosen by user id, or by another of the
 * user's memberships, can therefore 404 on everything.
 * `formContext.workItems` carries every team's items and so cannot answer
 * this; team scope belongs here rather than at each call site.
 */
export function activeTeamWorkItem(
  formContext: Pick<FormContextData, "activeTeamworkWorkItem">,
  user: Pick<ExtendedThalamosUser, "sessionContext">,
): FormContextData["activeTeamworkWorkItem"] {
  const workItem = formContext.activeTeamworkWorkItem;

  // A mismatch means the context was loaded under a different team from the one
  // now active, so the item it names is not this session's to act on.
  return workItem?.teamId === user.sessionContext?.teamId
    ? workItem
    : undefined;
}

/**
 * The work item a "no medical recommendation" decision may be written to, or
 * the reason there is none.
 *
 * The decision completes the item it is written to, so an item held by another
 * team, or one `workItemIsAnotherWorkflow` can place elsewhere, is refused.
 * Whoever offers the decision and whoever records it must reach the same
 * answer, so the rule lives here rather than at each of them.
 *
 * A context holding no item for this team refuses as `wrong-team` as well:
 * from the session's side, work it cannot see and work it does not hold are
 * the same thing.
 */
// TODO(no-med-rec): validate the target work item's workflow at the API
// boundary.
export function noMedRecTarget(
  formContext: Pick<FormContextData, "activeTeamworkWorkItem" | "forms">,
  user: Pick<ExtendedThalamosUser, "sessionContext">,
): {
  workItem?: FormContextData["activeTeamworkWorkItem"];
  refusal?: "wrong-team" | "other-workflow";
} {
  const workItem = activeTeamWorkItem(formContext, user);

  if (workItem === undefined) {
    return { refusal: "wrong-team" };
  }

  return workItemIsAnotherWorkflow(workItem, formContext.forms)
    ? { refusal: "other-workflow" }
    : { workItem };
}

/**
 * Redo (non-s15): an application's "supported-by" link to a med rec is
 * auto-severed when that med rec is redone, so the sign page's retained-link
 * basisInRedo check (which keys off the form's own links) cannot see it — unlike
 * an H3, whose "acknowledges" link to an application being redone is retained and
 * therefore IS caught. Detect a med rec in the admission being redone directly so
 * an application warns at sign exactly like an H3 does. Returns false for non-
 * applications (the retained-link check already covers those).
 */
export function applicationBasisInRedo(
  formBeingSigned: FormContextMiniForm | undefined,
  contextForms: FormContextMiniForm[],
): boolean {
  if (!formBeingSigned || !formIsApplication(formBeingSigned)) {
    return false;
  }
  return contextForms.some((f) => formIsMedRec(f) && formIsUnderRedo(f));
}

/**
 * The fields redo authorship is decided from. Drawn from `WorkItemMiniForm`
 * because it is the looser of the two mini-forms — its signatures omit their
 * base64 images — so a `FormContextMiniForm` satisfies it too. It carries no
 * `ownerUserId`, hence that field is optional and taken from the richer form;
 * where it is absent, team ownership decides an application's authorship.
 */
export type RedoAuthorshipForm = Simplify<
  Pick<
    WorkItemMiniForm,
    "formTemplate" | "latestVersion" | "versions" | "ownerTeamId"
  > &
    Partial<Pick<FormContextMiniForm, "ownerUserId">>
>;

/**
 * Redo authorship (Layer A): who may author a redo on a completed form — i.e.
 * start/respond-to/cancel it (the controls gated by `isRedoAuthor`). A medical
 * recommendation may only be redone by its FIRST signatory (the lead doctor; for
 * a joint med rec the Part-1 doctor only — Part-2 falls through to "Request
 * Redo"). An application is team-wide, so authorship defers to whether the user
 * owns or is on the assigned team. The backend applies its own author gate; this
 * governs which actions the UI offers.
 *
 * Typed to the fields it reads rather than a specific mini-form, so it serves
 * both the admission's forms and the leaner ones carried on a work item.
 */
export function userIsRedoAuthor(
  form: RedoAuthorshipForm,
  userId: UUID,
  isFormOwnerOrAssignedTeam: boolean,
): boolean {
  return formIsMedRec(form)
    ? isFirstSignatory(form, userId)
    : isFormOwnerOrAssignedTeam;
}

/**
 * `userIsRedoAuthor` with the application ownership test derived from the form
 * rather than passed in, for callers that hold the user's team rather than a
 * precomputed answer. Keeps every surface deciding authorship the same way.
 */
export function userIsRedoAuthorForTeam(
  form: RedoAuthorshipForm,
  userId: UUID,
  userTeamId: UUID | undefined,
): boolean {
  return userIsRedoAuthor(
    form,
    userId,
    form.ownerUserId === userId ||
      (!!userTeamId && userTeamId === form.ownerTeamId),
  );
}

/**
 * A redo replacement is re-signed part-for-part by the ORIGINAL form's
 * signatories, in the same order. This resolves whether `userId` is the person
 * who should sign the replacement's next unsigned part: for a joint med rec that
 * routes part 1 to the original Part-1 doctor and part 2 to the Part-2 doctor,
 * so a joint redo can only complete once both act. An application is team-wide
 * (single part, no per-part signatory), so it falls back to owning / being on
 * the assigned team. The backend enforces the part-for-part rule authoritatively
 * at sign time; this governs which controls the UI offers. If the original
 * cannot be resolved we permit (the backend remains the gate).
 */
export function userSignsNextReplacementPart(
  replacement: FormContextMiniForm,
  redoneFromForm: FormContextMiniForm | undefined,
  userId: UUID,
  isFormOwnerOrAssignedTeam: boolean,
): boolean {
  if (!formIsMedRec(replacement)) {
    return isFormOwnerOrAssignedTeam;
  }
  const nextPart = getLatestVersion(replacement).signatures.length;
  const originalPartSignatoryId = redoneFromForm
    ? getLatestVersion(redoneFromForm)?.signatures[nextPart]?.userId
    : undefined;
  return (
    originalPartSignatoryId === undefined || originalPartSignatoryId === userId
  );
}

/**
 * Whether "Cancel redo" may be offered on a form's row. Cancellation abandons a
 * form's OWN in-progress redo and returns it to its completed state, so it is
 * offered only when:
 *   - redo actions are available (not an archived / closed admission);
 *   - the current user holds the admission and authors this form's redo;
 *   - the form is under redo and has an in-progress replacement of its own;
 *   - no part of that replacement has been signed (a signed joint med rec part
 *     cannot be discarded, so the original cannot revert); and
 *   - every form this one relies on as its basis is still usable. Reverting to
 *     completed on a basis that has been voided would leave the form resting on
 *     something that no longer exists usably, so relinking and re-signing is the
 *     only route out of that. `redoBasisUpstreamIds` covers bases whose link was
 *     severed when they entered redo, which is where a voided basis is recorded.
 * The backend enforces the same rule authoritatively.
 */
export function canCancelRedo(args: {
  form: FormContextMiniForm;
  contextForms: FormContextMiniForm[];
  redoActionsAvailable: boolean;
  currentUserIsAssigned: boolean;
  isRedoAuthor: boolean;
  inProgressRedoReplacement: FormContextMiniForm | undefined;
}): boolean {
  const {
    form,
    contextForms,
    redoActionsAvailable,
    currentUserIsAssigned,
    isRedoAuthor,
    inProgressRedoReplacement,
  } = args;
  const upstreamBasisValid = redoBasisIsValid(
    redoBasisUpstreamIds(form, "basis").flatMap((id) => {
      const upstream = contextForms.find((f) => f.id === id);
      return upstream
        ? [{ status: upstream.status, redoState: upstream.redoState ?? null }]
        : [];
    }),
  );
  return (
    redoActionsAvailable &&
    currentUserIsAssigned &&
    form.redoState === "redo-in-progress" &&
    isRedoAuthor &&
    !!inProgressRedoReplacement &&
    !formHasSignatures(inProgressRedoReplacement) &&
    upstreamBasisValid
  );
}

/**
 * Whether "Retract redo request" may be offered on a form's row. Retraction
 * withdraws a pending request before the author responds, and is open to any
 * user holding their team's claim on the admission — not just the requester —
 * so it is offered only when:
 *   - redo actions are available (not an archived / closed admission);
 *   - the current user is the assigned holder of an accepted work item (the
 *     server 403s an unassigned caller, so a weaker gate would offer doomed
 *     actions);
 *   - a redo was requested directly on this form (an inherited redo-requested
 *     state has no request of its own to retract — that resolves through the
 *     upstream form); and
 *   - the current user is not the form's redo author, whose actions on a
 *     request are accept/decline instead.
 * The backend enforces these rules authoritatively (slightly looser in one
 * respect: an author may retract a request they raised themselves).
 */
export function canRetractRedo(args: {
  redoActionsAvailable: boolean;
  currentUserIsAssigned: boolean;
  admissionActivelyClaimed: boolean;
  redoState: FormContextMiniForm["redoState"];
  hasDirectRedoRequest: boolean;
  isRedoAuthor: boolean;
}): boolean {
  const {
    redoActionsAvailable,
    currentUserIsAssigned,
    admissionActivelyClaimed,
    redoState,
    hasDirectRedoRequest,
    isRedoAuthor,
  } = args;
  return (
    redoActionsAvailable &&
    currentUserIsAssigned &&
    admissionActivelyClaimed &&
    redoState === "redo-requested" &&
    hasDirectRedoRequest &&
    !isRedoAuthor
  );
}

export function computeAdmissionFormsState(allForms: FormContextMiniForm[]) {
  // Redo (non-s15): a voided form keeps status = "complete" but is superseded.
  // Exclude it from every workflow grouping (used/unused/medRecs/applications/
  // completion checks) and surface it separately so the detail page can show it
  // in a de-emphasised "Voided forms" section. previewableForms keeps the voided
  // forms so their VOID-watermarked PDF stays viewable in-page.
  const voidedForms = allForms.filter(formIsVoid);
  const forms = allForms.filter((f) => !formIsVoid(f));

  const medRecs = forms.filter(formIsMedRec);
  const applications = forms.filter(formIsApplication);
  const recordOfDetentions = forms.filter(formIsRecordOfDetention);
  const recordOfDetentionsInProgress = recordOfDetentions.find(
    (d) => d.status === "in-progress",
  );

  const admissionHasCompletedApplication = getCompletedApplication(forms);
  const admissionHasCompletedRecordOfDetention =
    recordOfDetentions.find(formIsCompleted);
  const signedPartOneRecordOfDetention =
    recordOfDetentions.find(formHasSignatures);
  const patientAdmissionDateTime = (
    getLatestVersion(signedPartOneRecordOfDetention)?.data[0].data as
      H3Part1DataSchema | undefined
  )?.admissionDateTime;

  // Forms grouped by completion status
  const formCanBeWorkedOn = (f: WorkItemMiniForm) => {
    return (
      f.latestVersion > 1 || // amend
      formIsRecordOfDetention(f) ||
      (formIsApplication(f) && !signedPartOneRecordOfDetention) ||
      (formIsMedRec(f) && !signedPartOneRecordOfDetention)
    );
  };
  const completeForms = forms
    .filter((f) => f.status === "complete" || f.status === "finalised")
    .sort((f1, f2) => (f1.updated < f2.updated ? 1 : -1));
  const allInProgressForms = forms
    .filter(formIsInProgress)
    .filter(formCanBeWorkedOn);
  // The original's row carries the "Continue redo"/"Cancel redo" actions, so a
  // collapsed replacement must NOT also render as its own in-progress row. They
  // are kept in a separate list so the chain-redo "start your replacement"
  // prompt can still find them.
  const redoReplacementForms = allInProgressForms.filter((f) =>
    formIsCollapsedRedoReplacement(f, forms),
  );
  const inProgressForms = allInProgressForms.filter(
    (f) => !redoReplacementForms.includes(f),
  );
  // A joint med rec redo replacement whose part 1 has been signed still needs
  // its part 2 signing by the second author, exactly like a first-time joint
  // med rec. It stays collapsed out of inProgressForms (no row of its own), but
  // it must still surface the "complete Part 2" send/continue action, so it is
  // folded back in here alongside the ordinary part-1-signed forms.
  const needsPart2Forms = [...inProgressForms, ...redoReplacementForms].filter(
    formHasSignatures,
  );

  const completedRecordOfDetention = completeForms.find(
    formIsRecordOfDetention,
  );

  // Get the ID of the linked ids (if it exists)
  const linkedIdsRecordOfDetention =
    signedPartOneRecordOfDetention?.linkedForms?.map((form) => form.id) || [];

  const linkedFormToActiveApplication = forms.find((form) =>
    linkedIdsRecordOfDetention.includes(form.id),
  );
  const linkedIdsOfActiveApplication =
    linkedFormToActiveApplication?.linkedForms.map((l) => l.id);

  const firstApplicationSignature =
    admissionHasCompletedApplication?.versions?.[0]?.signatures?.[0];

  const unusedForms = completeForms.filter((f) => {
    // A form under redo (its links are temporarily severed during the redo) is
    // not "unused" — it is mid-redo and stays in the completed list with its
    // "Redo requested/in progress" status. Without this, an auto-unlinked med rec
    // would be misfiled as unused.
    if (formIsUnderRedo(f)) {
      return false;
    }

    const wasCreatedBeforeCompletedApplication =
      firstApplicationSignature !== undefined &&
      dayjs(f.created).isBefore(
        dayjs(firstApplicationSignature.legalSignatureDate),
      );

    // If med rec has no linked form ID and the application is signed = unused form
    if (
      formIsMedRec(f) &&
      f.linkedForms.length === 0 &&
      admissionHasCompletedApplication &&
      wasCreatedBeforeCompletedApplication
    ) {
      return true;
    }
    // If application is not linked to signed H3 = unused form
    if (
      formIsApplication(f) &&
      signedPartOneRecordOfDetention &&
      !!f.linkedForms.length &&
      !linkedIdsRecordOfDetention.includes(f.id)
    ) {
      return true;
    }

    const wasCreatedBeforeCompletedRecordOfDetention =
      signedPartOneRecordOfDetention &&
      dayjs(f.created).isBefore(dayjs(signedPartOneRecordOfDetention.created));

    // If med rec is linked to an application that is not linked to signed H3 = unused forms
    if (
      formIsMedRec(f) &&
      signedPartOneRecordOfDetention &&
      wasCreatedBeforeCompletedApplication &&
      wasCreatedBeforeCompletedRecordOfDetention &&
      !!f.linkedForms.length &&
      !linkedIdsOfActiveApplication?.includes(f.id)
    ) {
      return true;
    }

    return false;
  });

  const usedForms = admissionHasCompletedApplication
    ? completeForms.filter((f) => !unusedForms.includes(f))
    : completeForms;

  const previewableForms = allForms
    .filter(formHasSignatures)
    .filter(
      (f) =>
        formIsCompleted(f) || (formIsInProgress(f) && formCanBeWorkedOn(f)),
    )
    .sort(firstBy("updated"));

  const uploadableForms = [...usedForms, ...unusedForms].filter(
    (f) => getLatestVersion(f).pdfUri,
  );

  return {
    medRecs,
    applications,
    recordOfDetentions,

    admissionHasCompletedApplication,
    admissionHasCompletedRecordOfDetention,
    recordOfDetentionsInProgress,
    signedPartOneRecordOfDetention,
    completedRecordOfDetention,
    patientAdmissionDateTime,

    completeForms,
    inProgressForms,
    redoReplacementForms,
    needsPart2Forms,
    usedForms,
    unusedForms,
    voidedForms,
    previewableForms,
    uploadableForms,
  };
}

function computePerFormData(
  userAndWorkItemState: UserAndWorkItemState,
  form: FormContextMiniForm,
  // When `form` is a redo replacement, the original form it replaced (resolved
  // via redoneFromFormId) so we can pin each part to its original signatory.
  redoneFromForm: FormContextMiniForm | undefined,
  patientAdmissionDateTime: string | undefined,
  deMinimisEnabled: boolean,
  redoFormsEnabled: boolean,
  recordOfDetentionSigned: boolean,
) {
  const { userId, amMha, myActiveWorkItem, matchesSigningReqs } =
    userAndWorkItemState;

  const template = getBaseFormTemplate(form)!;
  const fullFormName = getFullFormName(template);
  const latestVersion = getLatestVersion(form);
  const myFormWorkItem = myActiveWorkItem?.forms.find((f) => f.id === form.id);

  const isFormFinalised = form.status === "finalised";

  // Form type and status
  const isMedicalRecommendation = formIsMedRec(form);
  const isApplication = formIsApplication(form);
  const isRecordOfDetention = formIsRecordOfDetention(form);
  const formInProgress = form.status === "in-progress";
  const formNeedsFinalise = form.status === "complete";

  // Signature state
  const latestVersionHasSignatures = latestVersion.signatures.length > 0;
  const formNeedsMoreSignatures = latestVersionHasSignatures && formInProgress;
  const signedLastPart =
    latestVersion.signatures.length &&
    latestVersion.signatures.at(-1)?.userId === userId;

  // Permissions on next form part
  const nextPart = latestVersion.signatures.length;
  const nextPartTemplate = template.parts[nextPart] as
    FormPartTemplate<any> | undefined;
  const nextPartSigningReqs = nextPartTemplate?.signing.userOrTeamRequirement;
  const userCanSignFormType =
    !!nextPartSigningReqs && matchesSigningReqs(nextPartSigningReqs);

  // Amend status: if the form is an amend (has previous versions), then the user should only
  // be able to work on the form if they signed the next form part in the original version of the form
  const isAmend = form.versions.length > 1;
  const originalVersion = isAmend ? form.versions[0] : undefined;
  const originalVersionSig = originalVersion?.signatures[nextPart];
  const signedOriginalVersionOfPart = originalVersionSig?.userId === userId;

  // Layer B (redo, non-s15): a med rec redo replacement must be re-signed by the
  // same doctors in the same part order as the original. Restrict each part of
  // the replacement to that original part's signatory — for a joint med rec this
  // routes Part 2 to the original Part-2 doctor. Applications are team-wide so are
  // not restricted. The backend enforces this authoritatively at sign time; this
  // just hides the action from the wrong doctor. If the original cannot be
  // resolved we fall back to permitting (the backend remains the gate).
  const isRedoReplacement = !!form.redoneFromFormId;
  const redoOriginalPartSignatoryId =
    isRedoReplacement && redoneFromForm
      ? getLatestVersion(redoneFromForm)?.signatures[nextPart]?.userId
      : undefined;
  const canWorkOnRedoReplacementPart =
    !isMedicalRecommendation ||
    !isRedoReplacement ||
    redoOriginalPartSignatoryId === undefined ||
    redoOriginalPartSignatoryId === userId;

  const draftIdOnNextPart = myFormWorkItem?.formDraftId ?? null;
  const canWorkOnNextPart =
    formInProgress &&
    !!myActiveWorkItem &&
    (!isAmend || signedOriginalVersionOfPart) &&
    canWorkOnRedoReplacementPart &&
    (!signedLastPart || ["acd", "mha-h3"].includes(template.id)) &&
    userCanSignFormType &&
    (myActiveWorkItem?.formId === form.id ||
      isMedicalRecommendation ||
      isRecordOfDetention);

  // For med recs, hide "Start" when another user already has a draft in
  // progress on the same form. Only applies to Part 1 — joint med recs
  // allow a second doctor to start Part 2.
  const otherUserHasDraft =
    isMedicalRecommendation &&
    nextPart === 0 &&
    (form.allDrafts?.some(
      (d) => d.ownerUserId !== userId && d.status === "in-progress",
    ) ??
      false);

  const canLaunchDraft =
    canWorkOnNextPart && !draftIdOnNextPart && !otherUserHasDraft;

  // Compute whether form is within S15 amends period
  let isWithinSection15AmendPeriod = false;
  if (patientAdmissionDateTime && (isApplication || isMedicalRecommendation)) {
    let expiryPeriod;
    if (["mha-a11", "mha-a10"].includes(form.formTemplate.id)) {
      expiryPeriod = config.formExpiryPeriods.s15AmendSection4Expiry;
    } else {
      expiryPeriod = config.formExpiryPeriods.s15AmendSection2And3Expiry;
    }
    const now = dayjs();
    const hasExpired = dayjs(patientAdmissionDateTime)
      .add(expiryPeriod.value, expiryPeriod.unit)
      .isBefore(now);

    isWithinSection15AmendPeriod = !hasExpired;
  }

  const canRequestAmend =
    amMha &&
    formNeedsFinalise &&
    !!myActiveWorkItem &&
    isWithinSection15AmendPeriod &&
    (isMedicalRecommendation || isApplication);

  const canRequestDeMinimis =
    amMha &&
    formNeedsFinalise &&
    !!myActiveWorkItem &&
    deMinimisEnabled &&
    formIsDeMinimisEligible(form);

  // Redo (non-s15): available to any admission member with an active work item,
  // on a completed med rec / application that is not already under redo, before
  // the record of detention (H3) is signed. The backend is authoritative (it
  // also enforces the org flag, eligibility and the H3 hard stop); this flag
  // just governs whether the action is offered in the UI.
  const canRequestRedo =
    redoFormsEnabled &&
    !!myActiveWorkItem &&
    !recordOfDetentionSigned &&
    formIsValidForProgression(form) &&
    (isMedicalRecommendation || isApplication);

  // Eligibility for the redo escape hatch: a completed application already in a
  // redo holding state, where redoing it is the way out. This is the counterpart
  // to canRequestRedo (which requires the form to be valid for progression).
  //
  // Deliberately NOT offered at redo-requested: while a request is outstanding,
  // retracting or declining it returns the application to completed with nothing
  // to re-sign, so answering the request is the shorter route and must be taken
  // first. Once in progress there is no longer a cheap answer available.
  //
  // Like canRequestRedo this is an eligibility flag, not a green light on its
  // own: the caller must also gate on authorship and on the state being
  // inherited rather than a redo of the application's own.
  const canRedoApplicationUnderRedo =
    redoFormsEnabled &&
    !!myActiveWorkItem &&
    !recordOfDetentionSigned &&
    isApplication &&
    formIsCompleted(form) &&
    form.redoState === "redo-in-progress";

  // Extract generic form data (for use in views)
  const genericFormData =
    latestVersion.data.length > 0
      ? template!.extractGenericData(latestVersion.data)
      : { admissions: {} };

  return {
    template,
    fullFormName,
    genericFormData,
    isMedicalRecommendation,
    latestVersionHasSignatures,
    formNeedsMoreSignatures,
    myFormWorkItem,
    canLaunchDraft,
    isAmend,
    canRequestAmend,
    canRequestDeMinimis,
    canRequestRedo,
    canRedoApplicationUnderRedo,
    isFormFinalised,
    draftIdOnNextPart,
  };
}

export function admissionFormContextLogic(
  formContext: Omit<FormContextData, "patient" | "lastFormUpdated">,
  user: ExtendedThalamosUser,
) {
  const isAdmissionClosed = formContext.status === "closed";

  // True when the admission is no longer in active workflow, in which case it is
  // read-only for redo: redo statuses stay visible but no redo action may be
  // taken. Distinct from isAdmissionClosed, which is only the fully-closed
  // subset — an admission can leave active workflow without being closed.
  const isAdmissionArchivedOrClosed =
    isAdmissionClosed ||
    hasNoActiveWorkItemOrRejectedCancelledOrCompleted(formContext);

  const userAndWorkItemState = computeUserAndWorkItemState(
    user,
    formContext?.activeTeamworkWorkItem,
  );
  const {
    teamActiveWorkItem,
    teamActiveOrArchivedWorkItem,
    myActiveWorkItem,
    amAmhpUser,
    amDoctor,
    amMha,
    matchesSigningReqs,
  } = userAndWorkItemState;

  const hasActiveAmend = formContext?.workItems.some(
    (wi) =>
      (wi.requestType === "amend" && wi.status === "pending") ||
      (wi.requestType === "amend" &&
        wi.status === "accepted" &&
        formContext.forms.find((f) => wi.formId === f.id)?.status ===
          "in-progress"),
  );

  const hasActiveDeMinimis = formContext?.workItems.some(
    (wi) =>
      wi.requestType === "de-minimis" &&
      (wi.status === "pending" || wi.status === "accepted"),
  );

  const canWorkOnForms = !!myActiveWorkItem && !isAdmissionClosed;
  const canClaim =
    !!teamActiveWorkItem && !myActiveWorkItem && !isAdmissionClosed;
  const canSendOrShareAdmission = !!teamActiveOrArchivedWorkItem;

  // Admission form state
  const {
    admissionHasCompletedApplication,
    admissionHasCompletedRecordOfDetention,
    signedPartOneRecordOfDetention,
    completedRecordOfDetention,
    recordOfDetentionsInProgress,
    patientAdmissionDateTime,
    inProgressForms,
    redoReplacementForms,
    needsPart2Forms,
    usedForms,
    unusedForms,
    voidedForms,
    previewableForms,
    uploadableForms,
  } = computeAdmissionFormsState(formContext.forms);

  const userCanSignMedRec = firstPartSigningReqs(
    medicalRecommendationTemplateIds,
  ).some(matchesSigningReqs);

  const userCanSignApplication = firstPartSigningReqs(
    applicationTemplateIds,
  ).some(matchesSigningReqs);

  const userCanSignRecordOfDetention = firstPartSigningReqs(
    recordOfDetentionTemplateIds,
  ).some(matchesSigningReqs);

  // Medical reccomendations
  const medRecCanBeCreated = !admissionHasCompletedRecordOfDetention;
  const canLaunchMedicalRecommendation =
    canWorkOnForms && medRecCanBeCreated && userCanSignMedRec;
  const canRequestMedicalRecommendation =
    canWorkOnForms && medRecCanBeCreated && !userCanSignMedRec;
  const showDisabledLaunchMedRecButton =
    canClaim && medRecCanBeCreated && userCanSignMedRec;
  const showDisabledRequestMedRecButton =
    canClaim && medRecCanBeCreated && !userCanSignMedRec;

  // Applications
  const applicationCanBeCreated = !signedPartOneRecordOfDetention;
  const canLaunchApplication =
    canWorkOnForms && applicationCanBeCreated && userCanSignApplication;
  const canRequestApplication =
    canWorkOnForms &&
    applicationCanBeCreated &&
    !userCanSignApplication &&
    !amDoctor;
  const showDisabledLaunchApplicationButton =
    canClaim && applicationCanBeCreated && userCanSignApplication;
  const showDisabledRequestApplicationButton =
    canClaim && applicationCanBeCreated && !userCanSignApplication;

  // Record of Detention — see canCreateRecordOfDetention for why the launch
  // button is offered regardless of an application's redo state.
  const recordOfDetentionCanBeCreated = canCreateRecordOfDetention(
    formContext.forms,
  );
  const canLaunchRecordOfDetention =
    canWorkOnForms &&
    recordOfDetentionCanBeCreated &&
    userCanSignRecordOfDetention;
  const showDisabledLaunchRecordOfDetentionButton =
    canClaim && recordOfDetentionCanBeCreated && userCanSignRecordOfDetention;

  // Scrutiny
  const admissionNeedsScrutiny =
    admissionHasCompletedRecordOfDetention?.status === "complete";
  const canScrutiniseAdmission = !!(
    admissionNeedsScrutiny &&
    myActiveWorkItem &&
    amMha
  );

  const deMinimisEnabled =
    !!user.sessionOrganisationConfiguration?.deMinimisCorrectionsEnabled;
  const redoFormsEnabled =
    !!user.sessionOrganisationConfiguration?.redoFormsEnabled;

  // Per-form data
  const formData = new Map<string, AdmissionsFormData>();
  for (const form of formContext.forms) {
    formData.set(
      form.id,
      computePerFormData(
        userAndWorkItemState,
        form,
        form.redoneFromFormId
          ? formContext.forms.find((f) => f.id === form.redoneFromFormId)
          : undefined,
        patientAdmissionDateTime,
        deMinimisEnabled,
        redoFormsEnabled,
        !!signedPartOneRecordOfDetention,
      ),
    );
  }

  const mayUploadFormsToExternalSystem = amMha;

  return {
    admissionHasCompletedApplication,
    admissionHasCompletedRecordOfDetention,

    amAssignedToWorkItem: !!myActiveWorkItem,

    hasActiveAmend,
    hasActiveDeMinimis,

    admissionNeedsScrutiny,
    needsPart2Forms,
    amAmhpUser,
    amDoctor,
    isAdmissionClosed,
    isAdmissionArchivedOrClosed,
    signedPartOneRecordOfDetention,
    recordOfDetentionsInProgress,
    completedRecordOfDetention,

    // Forms
    inProgressForms,
    redoReplacementForms,
    unusedForms,
    usedForms,
    voidedForms,
    previewableForms,
    formData,
    uploadableForms,

    // Actions
    canScrutiniseAdmission,
    canSendOrShareAdmission,
    canLaunchMedicalRecommendation,
    canRequestMedicalRecommendation,
    canLaunchApplication,
    canRequestApplication,
    canLaunchRecordOfDetention,
    mayUploadFormsToExternalSystem,
    showDisabledLaunchMedRecButton,
    showDisabledLaunchApplicationButton,
    showDisabledRequestMedRecButton,
    showDisabledRequestApplicationButton,
    showDisabledLaunchRecordOfDetentionButton,
  };
}

export function standaloneFormContextLogic(
  formContext: Omit<FormContextData, "patient" | "lastFormUpdated">,
  user: ExtendedThalamosUser,
) {
  const form = formContext.forms[0];
  if (form === undefined) {
    throw new Error("Standalone form context has no form");
  }
  return singleFormContextLogic(formContext, form, user);
}

/**
 * The state of one form and the actions available on it, for form contexts whose
 * forms are each worked on independently rather than as parts of a whole (unlike
 * an admission, where a form's actions depend on the other forms present).
 */
export function singleFormContextLogic(
  formContext: Omit<FormContextData, "patient" | "lastFormUpdated">,
  form: FormContextMiniForm,
  user: ExtendedThalamosUser,
) {
  const formTemplate = getBaseFormTemplate(form)!;
  const latestVersion = getLatestVersion(form);

  const {
    teamActiveWorkItem,
    myActiveWorkItem,
    amGuest,
    amMha,
    matchesSigningReqs,
  } = computeUserAndWorkItemState(user, formContext?.activeTeamworkWorkItem);

  // Admission form state
  const { signedPartOneRecordOfDetention } = computeAdmissionFormsState(
    formContext.forms,
  );

  const fullFormName = getFullFormName(formTemplate);

  const formInProgress = form.status === "in-progress";
  const formNeedsFinalise = form.status === "complete";

  const formSigned = latestVersion.signatures.length > 0;
  const signedLastPart =
    latestVersion.signatures.length > 0 &&
    latestVersion.signatures.at(-1)?.userId === user.id;
  const nextPart = formTemplate.parts[latestVersion.signatures.length] as
    FormPartTemplate<any> | undefined;
  const nextPartSigningReqs = nextPart?.signing.userOrTeamRequirement;
  const userCanSignFormType =
    amGuest || (nextPartSigningReqs && matchesSigningReqs(nextPartSigningReqs));

  const draftIdOnNextPart =
    myActiveWorkItem?.forms.find((f) => f.id === form.id)?.formDraftId ?? null;
  const canContinueForm =
    formInProgress && draftIdOnNextPart && userCanSignFormType;
  const canWorkOnNextPart =
    formInProgress &&
    !!myActiveWorkItem &&
    (!signedLastPart || ["acd", "mha-h3"].includes(formTemplate.id)) &&
    userCanSignFormType;
  const canLaunchDraft = canWorkOnNextPart && !draftIdOnNextPart;

  const canDownloadForm = formSigned;
  const canUploadFormToExternalSystem =
    amMha && COMPLETED_FORM_STATUSES.includes(form.status);
  const isFormOwnerOrAssignedTeam =
    form.ownerUserId === user.id ||
    (!!user.sessionContext?.teamId &&
      user.sessionContext.teamId === form.ownerTeamId);
  const canDeleteForm =
    !!myActiveWorkItem && !formSigned && isFormOwnerOrAssignedTeam;
  const canFinaliseForm = formNeedsFinalise && !!myActiveWorkItem && amMha;
  const canSendForm =
    teamActiveWorkItem &&
    ((formInProgress && !!myActiveWorkItem && !canWorkOnNextPart) ||
      (formNeedsFinalise && !canFinaliseForm));

  const deMinimisEnabled =
    !!user.sessionOrganisationConfiguration?.deMinimisCorrectionsEnabled;
  const canRequestDeMinimis =
    amMha &&
    formNeedsFinalise &&
    !!myActiveWorkItem &&
    deMinimisEnabled &&
    formIsDeMinimisEligible(form);

  return {
    fullFormName,
    draftIdOnNextPart,
    formInProgress,
    formNeedsFinalise,
    signedLastPart,
    canFinaliseForm,
    canLaunchDraft,
    canDownloadForm,
    canUploadFormToExternalSystem,
    canDeleteForm,
    canContinueForm,
    canSendForm,
    canRequestDeMinimis,
    form,
    latestVersion,
    signedPartOneRecordOfDetention,
  };
}

/**
 * An MCA assessment holds an FDMCA, a Best Interest Decision, or both. Each form
 * is worked on independently, but the assessment is scrutinised as a whole, so
 * scrutiny is offered at assessment level once every form in it is complete —
 * never per form, which the server would reject.
 */
export function mcaFormContextLogic(
  formContext: Omit<FormContextData, "patient" | "lastFormUpdated">,
  user: ExtendedThalamosUser,
) {
  const { myActiveWorkItem, amMha, matchesSigningReqs } =
    computeUserAndWorkItemState(user, formContext.activeTeamworkWorkItem);

  const forms = liveMcaForms(formContext.forms);

  const formStates = forms.map((form) =>
    singleFormContextLogic(formContext, form, user),
  );

  const assessmentComplete = forms.length > 0 && forms.every(formIsCompleted);

  // A finalised assessment has already been scrutinised; only one still holding
  // a merely-complete form has scrutiny outstanding.
  const needsScrutiny = forms.some((f) => f.status === "complete");

  const prompt = mcaContextPrompt(forms);
  const promptTemplate = prompt ? getBaseFormTemplate(prompt.templateId) : null;

  // The prompt invites the user to complete the partner form themselves, so it
  // is only offered to someone who could sign that form.
  const promptSigning = promptTemplate?.parts[0]?.signing;
  const canStartPromptedForm =
    promptSigning !== undefined &&
    !!myActiveWorkItem &&
    matchesSigningReqs(promptSigning.userOrTeamRequirement);

  return {
    forms,
    formStates,
    canFinaliseAssessment:
      assessmentComplete && needsScrutiny && !!myActiveWorkItem && amMha,
    promptTemplateId: prompt?.templateId ?? null,
    canStartPromptedForm,
  };
}

export function canContinueAdmissionForm(
  draftIdOnNextPart: string | undefined,
  currentUserIsAssigned: boolean,
  completedRecordOfDetention: FormContextMiniForm | undefined,
  isAmend: boolean | undefined,
) {
  return (
    draftIdOnNextPart &&
    currentUserIsAssigned &&
    (!completedRecordOfDetention || isAmend)
  );
}

export function canStartAdmissionForm(
  canLaunchDraft: boolean | undefined,
  myFormWorkItem: WorkItemMiniForm | undefined,
  currentUserIsAssigned: boolean,
) {
  return canLaunchDraft && myFormWorkItem && currentUserIsAssigned;
}
